// ממשקים ושער (08 §1–2).
import { z } from 'zod';
import { Id, Json, Ts, Usage, rowBase } from './common.ts';

/** ממשק = ערוץ; הפעולה ב-action. 14: כל ערוץ (גם עתידי: 'mail' | 'whatsapp' | 'sms' | inbound) הוא ערך כאן + שורת sys.interface. */
/** `source` (14 §3, 03 §6) — הקונקטור כממשק: action `setup` (הפעלת Change Tracking) עם תופעת לוואי וקונסנט. */
/** 20: `mail.send` / `mail.read` — שני ממשקים נפרדים (שליחה: תופעת לוואי, היקף נמענים; קריאה: היקף תיבה). */
/** conversation-org-memory: `memory.org` — הזיכרון הארגוני; פעולות save/replace/forget = קונסנט של בעל ההרשאה. */
export type InterfaceId = 'data' | 'ai3' | 'web' | 'source' | 'tree' | 'users' | 'memory.org' | 'artifact.db' | 'erp.write' | 'mail.send' | 'mail.read' | `world.${string}`;
export const InterfaceId = z.union([
  z.enum(['data', 'ai3', 'web', 'source', 'tree', 'users', 'memory.org', 'artifact.db', 'erp.write', 'mail.send', 'mail.read']),
  z.templateLiteral(['world.', z.string().regex(/^[a-z][a-z0-9_]{1,30}$/)]),
]) as z.ZodType<InterfaceId>;

/** users-delegation: `users` — ניהול משתמשים כממשק; grant אליו = "הרשאת האצלה". */
export const InterfaceKind = z.enum(['data', 'ai', 'web', 'world', 'erp_write', 'connector', 'db', 'mail', 'whatsapp', 'sms', 'users', 'memory']);
export type InterfaceKind = z.infer<typeof InterfaceKind>;

/* ─── 14: היקף + תוקף — הבסיס המשותף לממשקים "scoped" (web: hosts; עתידי: mail/whatsapp — recipients) ─── */

/**
 * ההיקף עצמו. מפתח לכל סוג (config.scope.kind): hosts — שמות host, '*.x' = כל תת-דומיין וגם x; '*' = הכול.
 * 20: recipients — כתובות מייל, '*@domain' = כל כתובת בדומיין (לא תת-דומיינים), '*' = כל נמען; mailbox — 'self' (התיבה של ה-runAs) + since.
 */
export const ScopeSpec = z
  .object({
    hosts: z.union([z.literal('*'), z.array(z.string().min(1).max(253)).max(200)]).optional(),
    recipients: z.union([z.literal('*'), z.array(z.string().min(1).max(320)).max(500)]).optional(),
    mailbox: z.literal('self').optional(),
    since: Ts.optional(),
  })
  .passthrough();
export type ScopeSpec = z.infer<typeof ScopeSpec>;

/** מה הסוכן מבקש ומה האדם מאשר (sys.consent.scope_request). until=null — בלי תוקף (היקף ליצירה). */
export const ScopeRequest = z.object({
  scope: ScopeSpec,
  until: Ts.nullable(),
  purposeHe: z.string().max(500),
});
export type ScopeRequest = z.infer<typeof ScopeRequest>;

/** sys.interface_scope — "מותר עד כאן, עד מתי": למשתמש (שיחה, עם תוקף) או ליצירה (קוד, בלי תוקף). בדיוק אחד מהם. */
export const InterfaceScope = z.object({
  ...rowBase,
  interfaceId: InterfaceId,
  userId: Id.nullable(),
  artifactId: Id.nullable(),
  scope: ScopeSpec,
  purposeHe: z.string(),
  validUntil: Ts.nullable(),
  grantedBy: Id.nullable(),
  consentId: Id.nullable(),
  revokedAt: Ts.nullable(),
  revokedBy: Id.nullable(),
});
export type InterfaceScope = z.infer<typeof InterfaceScope>;

/** sys.interface.config — מה ה-registry מצהיר על ממשק (14 §2). הכול אופציונלי; ברירות המחדל בקוד השער לשורות ישנות. */
export const InterfaceConfig = z
  .object({
    nameHe: z.string().optional(),
    /** false/true קבוע; 'adapter' = לפי functions[fn].sideEffect של אדפטר העולם; 'actions' = לפי actions[action] (17 §3). */
    sideEffect: z.union([z.boolean(), z.literal('adapter'), z.literal('actions')]).optional(),
    /** 17 §3 "ממשק": תופעת לוואי לפי פעולה — boolean או {sideEffect, nameHe}; גובר על sideEffect של הממשק לפעולה שמופיעה כאן. */
    actions: z.record(z.string(), z.union([z.boolean(), z.object({ sideEffect: z.boolean(), nameHe: z.string().optional() }).passthrough()])).optional(),
    scope: z.object({ kind: z.enum(['hosts', 'recipients', 'mailbox', 'channels']), defaultHours: z.number().positive().nullable().optional() }).optional(),
    /** conversation-org-memory: admin פעיל מורשה בלי שורת grant (שלב 3ב ובדיקת הביצוע); לאחרים — grant רגיל. */
    adminAlways: z.boolean().optional(),
    quota: z.object({ callsPerDay: z.number().int().positive().optional(), bytesPerDay: z.number().int().positive().optional() }).optional(),
  })
  .passthrough();
export type InterfaceConfig = z.infer<typeof InterfaceConfig>;

/** sys.interface — PK הוא (instance_id, id). */
export const Interface = z.object({
  id: InterfaceId,
  instanceId: Id,
  createdAt: Ts,
  updatedAt: Ts.nullable(),
  kind: InterfaceKind,
  enabled: z.boolean(),
  killed: z.boolean(),
  consentable: z.boolean(),
  worldId: Id.nullable(),
  config: z.record(z.string(), z.unknown()),
  updatedBy: Id.nullable(),
});
export type Interface = z.infer<typeof Interface>;

/** sys.interface_grant */
export const InterfaceGrant = z.object({
  ...rowBase,
  userId: Id,
  interfaceId: InterfaceId,
  grantedBy: Id.nullable(),
  /** 20: admin ביטל (interfaces.revoke) — השורה נשארת כדי שהענקה אוטומטית (חיבור חשבון מייל) לא תחזיר; interfaces.grant מנקה. */
  revokedByAdmin: z.boolean(),
});
export type InterfaceGrant = z.infer<typeof InterfaceGrant>;

export const OriginKind = z.enum(['artifact_run', 'conversation', 'system']);
export type OriginKind = z.infer<typeof OriginKind>;

export const ConsentStatus = z.enum(['pending', 'approved', 'partial', 'rejected', 'expired']);
export type ConsentStatus = z.infer<typeof ConsentStatus>;

/** sys.consent */
export const Consent = z.object({
  ...rowBase,
  approverUserId: Id,
  requestedByUserId: Id,
  originKind: z.enum(['artifact_run', 'conversation']),
  originId: Id,
  interfaceId: InterfaceId,
  action: z.string(),
  titleHe: z.string(),
  status: ConsentStatus,
  itemsTotal: z.number().int().nonnegative(),
  expiresAt: Ts,
  decidedAt: Ts.nullable(),
  decidedBy: Id.nullable(),
  /** גרסת אדפטר העולם שהוצגה לאישור — היא שרצה בביצוע (08 §1); null לממשק שאינו world.* */
  adapterVersion: z.number().int().nullable(),
  /** 14: בקשת היקף (ממשק scoped) — האישור יוצר שורת interface_scope לפני ביצוע הפריטים. */
  scopeRequest: ScopeRequest.nullable().optional(),
});
export type Consent = z.infer<typeof Consent>;

export const ConsentItemStatus = z.enum(['pending', 'approved', 'rejected', 'executed', 'failed']);
export type ConsentItemStatus = z.infer<typeof ConsentItemStatus>;

/** sys.consent_item */
export const ConsentItem = z.object({
  ...rowBase,
  consentId: Id,
  seq: z.number().int().nonnegative(),
  summaryHe: z.string(),
  preview: Json,
  payload: Json,
  status: ConsentItemStatus,
  executedAt: Ts.nullable(),
  outcomeLogId: Id.nullable(),
});
export type ConsentItem = z.infer<typeof ConsentItem>;

export const ReasonCode = z.enum([
  'ok', 'replayed', 'invalid_request', 'missing_idempotency_key', 'actor_mismatch',
  'interface_unknown', 'interface_disabled', 'not_granted', 'killed', 'needs_consent',
  'consent_rejected', 'consent_expired', 'unavailable', 'quota_exceeded',
  // 20: אין חשבון מייל מחובר ל-runAs (או בלי היכולת) · נמען/תיבה מחוץ להצהרה של הקוד (לפני השער)
  'no_mailbox', 'not_in_scope',
  // artifacts-empty-screen: שליחה מריצה בלי תוכן (מסך ריק, בלי גוף ובלי קבצים) — דילוג, לא נשלח
  'empty_content',
]);
export type ReasonCode = z.infer<typeof ReasonCode>;

export const Decision = z.enum(['allowed', 'pending_consent', 'denied']);
export type Decision = z.infer<typeof Decision>;

export const LogPhase = z.enum(['intent', 'outcome']);
export type LogPhase = z.infer<typeof LogPhase>;

/** sys.interface_log — append-only, בלי updated_at. */
export const InterfaceLog = z.object({
  id: Id,
  instanceId: Id,
  createdAt: Ts,
  ts: Ts,
  phase: LogPhase,
  intentLogId: Id.nullable(),
  interfaceId: InterfaceId,
  action: z.string(),
  actorUserId: Id,
  runAsUserId: Id,
  originKind: OriginKind,
  originId: Id.nullable(),
  idempotencyKey: z.string().min(1),
  decision: Decision,
  reason: ReasonCode,
  consentId: Id.nullable(),
  itemsCount: z.number().int().nonnegative(),
  /** הגוף לא נשמר — רק hash. */
  payloadHash: z.string(),
  status: z.enum(['ok', 'failed']).nullable(),
  /** answer | value_not_found | unsupported | unavailable | blocked | ok … */
  resultKind: z.string().nullable(),
  durationMs: z.number().int().nullable(),
  usage: Usage.nullable(),
  errorHe: z.string().nullable(),
});
export type InterfaceLog = z.infer<typeof InterfaceLog>;

/** קלט ל-appendInterfaceLog: בלי id/createdAt; instanceId מה-Db אם חסר. */
export const InterfaceLogInsert = InterfaceLog.omit({ id: true, createdAt: true, instanceId: true }).extend({
  instanceId: Id.optional(),
  ts: Ts.optional(),
  intentLogId: Id.nullable().optional(),
  originId: Id.nullable().optional(),
  consentId: Id.nullable().optional(),
  itemsCount: z.number().int().nonnegative().optional(),
  status: z.enum(['ok', 'failed']).nullable().optional(),
  resultKind: z.string().nullable().optional(),
  durationMs: z.number().int().nullable().optional(),
  usage: Usage.nullable().optional(),
  errorHe: z.string().nullable().optional(),
});
export type InterfaceLogInsert = z.input<typeof InterfaceLogInsert>;

export const GateRequest = z.object({
  instanceId: Id,
  actorUserId: Id,
  runAsUserId: Id,
  origin: z.object({ kind: OriginKind, id: z.string() }),
  interfaceId: InterfaceId,
  action: z.string().min(1),
  idempotencyKey: z.string().min(1),
  items: z.array(z.object({ summaryHe: z.string(), preview: z.unknown(), payload: z.unknown() })),
});
export type GateRequest = z.infer<typeof GateRequest>;

export const GateDecision = z.object({
  decision: Decision,
  reason: ReasonCode,
  logId: Id,
  consentId: Id.optional(),
  replayed: z.boolean(),
  outcomeLogId: Id.optional(),
  /** ב-replay של allowed: מצב הביצוע המקורי. רק 'ok' הוא replay של הצלחה; 'failed' → הכשל המקורי; 'pending' → עוד רץ. */
  outcomeStatus: z.enum(['ok', 'failed', 'pending']).optional(),
});
export type GateDecision = z.infer<typeof GateDecision>;
